import { Chip, ChipSize } from "@amzn/stencil-react-components/chip";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, Flex } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { SearchField } from "@amzn/uxm-stencil-react-components/lib/src/stencil/search-field";
import { getString } from "common/uistringlabels/uiStringUtils";
import { debounce } from "lib/helpers";
import { UserPermissions } from "lib/services";
import { ItemModel } from "lib/services/catalogical-models";
import { CatalogicalService } from "lib/services/catalogical-service";
import { UserInformation } from "lib/services/user-information-service";
import React, { FC, useEffect, useMemo, useState } from "react";

import { useUseCaseMetadata } from "../hooks/useUseCaseMetadata";
import { STEP_VALIDATORS } from "./form-validation";
import { FormField, FormFieldConfig } from "./FormField";
import { StepActions } from "./StepActions";
import { StepProps } from "./StepProps";

const BASIC_FIELDS: FormFieldConfig[] = [
  {
    id: "use-case-title",
    name: "title",
    type: "text",
    labelKey: "useCases.contributeForm.labelTitle",
    placeholderKey: "useCases.contributeForm.titlePlaceholder",
    required: true,
    maxLength: 120,
  },
  {
    id: "short-description",
    name: "shortDescription",
    type: "textarea",
    labelKey: "useCases.contributeForm.labelShortDescription",
    placeholderKey: "useCases.contributeForm.shortDescriptionPlaceholder",
    required: true,
    rows: 5,
    maxLength: 500,
  },
];

export const BasicInformationStep: FC<StepProps> = ({ formData, onChange, onNext, onSaveDraft }) => {
  const { categories, audiences } = useUseCaseMetadata();
  const [toolQuery, setToolQuery] = useState("");
  const [toolResults, setToolResults] = useState<ItemModel[]>([]);
  const [selectedTools, setSelectedTools] = useState<ItemModel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(true);
  const service = useMemo(() => new CatalogicalService<ItemModel>("aiforme"), []);

  const searchTools = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 2) {
          setToolResults([]);
          return [];
        }
        setIsSearching(true);
        try {
          const res = await service.getItems({
            pageSize: 10,
            searchText: query,
            sortBy: ["title"],
            sortDirection: ["asc"],
          });
          setToolResults(res?.data || []);
        } catch {
          setToolResults([]);
        } finally {
          setIsSearching(false);
        }
        return [];
      }, 400)[0],
    [service]
  );

  useEffect(() => {
    if (toolQuery) searchTools(toolQuery);
    else setToolResults([]);
  }, [toolQuery]);

  useEffect(() => {
    if (formData.toolsUsed.length > 0 && selectedTools.length === 0) {
      service
        .getItems({ filters: { taxonomyId: formData.toolsUsed }, sortBy: [], sortDirection: [] })
        .then((res) => {
          if (res?.data) setSelectedTools(res.data);
        })
        .catch(() => {});
    }
  }, []);

  const handleSelectTool = (tool: ItemModel) => {
    if (!selectedTools.some((t) => t.taxonomyId === tool.taxonomyId)) {
      const updated = [...selectedTools, tool];
      setSelectedTools(updated);
      onChange({ toolsUsed: updated.map((t) => t.taxonomyId) });
    }
    setToolQuery("");
    setShowDropdown(false);
  };

  const handleRemoveTool = (tool: ItemModel) => {
    const updated = selectedTools.filter((t) => t.taxonomyId !== tool.taxonomyId);
    setSelectedTools(updated);
    onChange({ toolsUsed: updated.map((t) => t.taxonomyId) });
  };

  const userInfo = UserInformation.current.getCachedAmazonianInformation();
  const contributorName = userInfo
    ? `${userInfo.basicInfo?.firstName || ""} ${userInfo.basicInfo?.lastName || ""}`.trim()
    : "";

  const isValid = STEP_VALIDATORS.basicInformation(formData);

  return (
    <Col gridGap="S400">
      <Text fontSize="T400" fontWeight="bold">
        {getString("useCases.contributeForm.basicInformation")}
      </Text>

      <div className="contribute-use-case-page__field-group">
        <Text fontWeight="bold">{getString("useCases.contributeForm.contributor")}</Text>
        <Text fontSize="T100" color="var(--st-dt-color-text-secondary)">
          {getString("useCases.contributeForm.contributorAutoPopulated")}
        </Text>
        <Text fontSize="T200">
          {contributorName} <b>({UserPermissions.current.username})</b>
        </Text>
      </div>

      {BASIC_FIELDS.map((field) => (
        <FormField
          key={field.id}
          config={field}
          value={(formData as any)[field.name]}
          onChange={(v) => onChange({ [field.name]: v })}
        />
      ))}

      {/* Custom: Tool search with chips */}
      <InputWrapper labelText={getString("useCases.contributeForm.labelToolsUsed")} id="tools-used" required>
        {(inputProps) => (
          <>
            <SearchField
              touchpoint="use-case-tools-search"
              query={toolQuery}
              onChange={(q) => {
                setToolQuery(q);
                setShowDropdown(true);
              }}
              renderResult={showDropdown ? (result: ItemModel) => result.title || result.taxonomyId : undefined}
              results={showDropdown ? toolResults : []}
              onResultClick={(result) => handleSelectTool(result)}
              shouldShowListOnNoResults={showDropdown}
              isLoading={showDropdown ? isSearching : false}
              placeholder={getString("useCases.contributeForm.toolsUsedPlaceholder")}
              {...inputProps}
            />
            <Flex flexDirection="row" gridGap="S200" flexWrap="wrap">
              {selectedTools.map((tool) => (
                <Chip key={tool.taxonomyId} size={ChipSize.Small} onDismiss={() => handleRemoveTool(tool)}>
                  {tool.title || tool.taxonomyId}
                </Chip>
              ))}
            </Flex>
          </>
        )}
      </InputWrapper>

      {/* Category uses FormField with dynamic options */}
      <FormField
        config={{
          id: "category",
          name: "categories",
          type: "multiSelect",
          labelKey: "useCases.contributeForm.labelCategory",
          placeholderKey: "useCases.contributeForm.categoriesPlaceholder",
          required: true,
          options: categories,
        }}
        value={formData.categories}
        onChange={(v) => onChange({ categories: v })}
      />

      <StepActions onSaveDraft={onSaveDraft} onNext={onNext} isNextDisabled={!isValid} />
    </Col>
  );
};
